import { useNavigate } from 'react-router-dom';
import { AgentAvatar, parseSeed, PALETTES } from '../AgentAvatar';
import { Sparkline } from '../shared/Sparkline';
import { formatCurrency, getPnlColor } from '../../utils/formatting';
import { isGraduationReady } from '../../utils/graduationCheck';

/**
 * Paper portfolio row — compact, de-emphasized.
 * Per wf-v3.jsx PaperRow.
 */
function PaperRow({ portfolio, detail, onOpen, onGraduate, onAssignAgent }) {
  const agent = portfolio.active_agent;
  const winner = isGraduationReady(detail);

  const totalValue = detail?.total_value_usd;
  const pnlPct = detail?.realized_pnl_pct;
  const winRate = detail?.win_rate;

  // Build sparkline from timeseries
  const sparkData = detail?.timeseries?.map(d => d.equity) || [];

  const { avatarSeed, paletteIndex } = agent?.avatar_seed
    ? parseSeed(agent.avatar_seed)
    : { avatarSeed: agent?.name || 'x', paletteIndex: 0 };

  return (
    <div
      onClick={() => onOpen(portfolio.id)}
      className={`flex items-center gap-3 px-3 py-2.5 border-b border-dashed border-slate-700/50 cursor-pointer hover:bg-slate-800/50 transition-colors ${
        winner ? 'bg-green-900/10' : ''
      }`}
    >
      {/* Avatar */}
      <div className="flex-shrink-0">
        {agent ? (
          <AgentAvatar
            name={agent.name}
            seed={avatarSeed}
            colors={PALETTES[paletteIndex] || PALETTES[0]}
            size={28}
          />
        ) : (
          <div className="w-7 h-7 rounded-full bg-slate-700 flex items-center justify-center text-xs text-slate-400">
            ?
          </div>
        )}
      </div>

      {/* Name + persona */}
      <div className="flex flex-col min-w-0 w-28 flex-shrink-0">
        <span className="text-sm font-semibold text-white truncate">{portfolio.name}</span>
        {agent?.name ? (
          <span className="text-xs text-slate-500 truncate">{agent.name}</span>
        ) : (
          <button
            onClick={(e) => { e.stopPropagation(); onAssignAgent?.(portfolio); }}
            className="inline-flex items-center gap-0.5 w-fit text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-amber-900/40 border border-amber-700/60 text-amber-400 hover:bg-amber-900/60 transition-colors"
          >
            <svg className="w-2.5 h-2.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            assign agent
          </button>
        )}
      </div>

      {/* Sparkline */}
      <div className="w-20 flex-shrink-0 hidden md:block">
        {sparkData.length > 1 ? (
          <Sparkline data={sparkData} width={80} height={22} negative={pnlPct < 0} fill={false} />
        ) : (
          <div className="h-5 w-full bg-slate-700/30 rounded animate-pulse" />
        )}
      </div>

      {/* Value */}
      <div className="w-20 flex-shrink-0 text-right">
        {totalValue != null ? (
          <span className="text-sm font-mono font-semibold text-white">{formatCurrency(totalValue)}</span>
        ) : (
          <div className="h-4 w-16 bg-slate-700 rounded animate-pulse ml-auto" />
        )}
      </div>

      {/* PnL */}
      <div className="w-20 flex-shrink-0 text-right hidden sm:block">
        {pnlPct != null ? (
          <span className={`text-sm font-mono font-semibold ${getPnlColor(pnlPct)}`}>
            {pnlPct >= 0 ? '+' : ''}{parseFloat(pnlPct).toFixed(1)}%
          </span>
        ) : (
          <div className="h-4 w-12 bg-slate-700 rounded animate-pulse ml-auto" />
        )}
      </div>

      {/* Strategy */}
      <div className="flex-1 min-w-0 hidden lg:block">
        <span className="text-xs text-slate-400 truncate block">
          {detail?.active_agent?.research_config?.bucket || '--'}
        </span>
      </div>

      {/* Graduation pill */}
      {winner && (
        <button
          onClick={(e) => { e.stopPropagation(); onGraduate(portfolio); }}
          className="flex-shrink-0 text-xs px-2 py-0.5 rounded bg-amber-600 text-white font-medium hover:bg-amber-500 transition-colors"
        >
          graduate to live &rarr;
        </button>
      )}

      {/* Open button */}
      <button
        onClick={(e) => { e.stopPropagation(); onOpen(portfolio.id); }}
        className="flex-shrink-0 hidden sm:inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-cyan-700/50 bg-cyan-900/20 hover:bg-cyan-800/40 hover:border-cyan-500 text-cyan-400 hover:text-cyan-300 transition-all text-xs font-medium"
        aria-label="Open workspace"
      >
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
        </svg>
        open
      </button>
    </div>
  );
}

/**
 * Practice portfolio table — de-emphasized section.
 * Per wf-v3.jsx DashboardV3 practice section.
 */
export function PracticePortfolioTable({ portfolios, portfolioDetails, onOpenWorkspace, onGraduate, onAssignAgent, onCreateNew, createForm }) {
  return (
    <section>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold text-slate-400">Practice portfolios</h2>
            <span className="text-xs px-2 py-0.5 rounded bg-slate-800 border border-slate-700 text-slate-500">
              paper &middot; sandbox
            </span>
          </div>
          <p className="text-xs text-slate-500 mt-0.5">
            Test strategies risk-free. Your best-performing paper can be graduated to live.
          </p>
        </div>
        {createForm}
      </div>

      {/* Table */}
      {portfolios.length === 0 ? (
        <div className="border border-dashed border-slate-700 rounded-lg p-6 text-center">
          <p className="text-sm text-slate-500 mb-2">No practice portfolios yet.</p>
          <button onClick={onCreateNew} className="text-cyan-400 hover:text-cyan-300 text-sm">
            Create one to get started
          </button>
        </div>
      ) : (
        <div className="border border-slate-700 rounded-lg overflow-hidden bg-slate-800/30">
          {/* Table header */}
          <div className="flex items-center gap-3 px-3 py-1.5 border-b border-slate-700 bg-slate-800/50 text-xs text-slate-500 uppercase tracking-wide">
            <div className="w-7 flex-shrink-0" />
            <div className="w-28 flex-shrink-0">name</div>
            <div className="w-20 flex-shrink-0 hidden md:block">curve</div>
            <div className="w-20 flex-shrink-0 text-right">value</div>
            <div className="w-20 flex-shrink-0 text-right hidden sm:block">P&L</div>
            <div className="flex-1 hidden lg:block">strategy</div>
            <div className="w-28" />
          </div>

          {/* Rows */}
          {portfolios.map(p => (
            <PaperRow
              key={p.id}
              portfolio={p}
              detail={portfolioDetails.get(p.id)}
              onOpen={onOpenWorkspace}
              onGraduate={onGraduate}
              onAssignAgent={onAssignAgent}
            />
          ))}

          {/* Footer */}
          <div
            onClick={onCreateNew}
            className="flex items-center justify-center px-3 py-2.5 bg-slate-800/20 cursor-pointer hover:bg-slate-800/40 transition-colors"
          >
            <span className="text-xs text-slate-500">
              + new practice portfolio &middot; $1,000 starting cash &middot; resets anytime
            </span>
          </div>
        </div>
      )}
    </section>
  );
}
